/**
 * Safely parses a date string from the backend.
 * All backend timestamps are currently stored as "naive" IST (UTC+5:30).
 * This helper ensures the browser treats them as IST and localizes them correctly.
 */
export const parseBackendDate = (dateString) => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;

  let isoString = String(dateString);
  
  // If the string represents a date+time and has no timezone info, assume IST (+05:30)
  // Example matches: "2024-04-22 10:00:00", "2024-04-22T10:00:00"
  // Example skips: "2024-04-22", "2024-04-22T04:30:00Z", "2024-04-22T04:30:00+05:30"
  const hasTime = isoString.includes(':');
  const hasTimezone = isoString.includes('Z') || isoString.includes('+') || /[-+]\d{2}:\d{2}$/.test(isoString);
  
  if (hasTime && !hasTimezone) {
    isoString = isoString.replace(' ', 'T');
    isoString = `${isoString}+05:30`;
  }
  
  const date = new Date(isoString);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (dateString) => {
  const date = parseBackendDate(dateString);
  if (!date) return dateString || 'N/A';
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (dateString) => {
  const date = parseBackendDate(dateString);
  if (!date) return 'N/A';

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

export const formatTimeAgo = (dateString) => {
  const date = parseBackendDate(dateString);
  if (!date) return 'N/A';
  
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
  }
  
  return 'Just now';
};

export const isOverdue = (dueDate) => {
  const date = parseBackendDate(dueDate);
  if (!date) return false;
  return date < new Date();
};